import { useSectionAccess } from "../../auth/useSectionAccess";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { tmApi, type TmTaskWatcherView } from "../../api/tm";
import { useAuth } from "../../auth/AuthContext";
import type { ApiError } from "../../api/client";
import { useToast } from "../Toast";

/**
 * Watch / Unwatch toggle for a task. Renders both the button and a
 * collapsible watchers list — clicking the count cycles the list open.
 * Optimistically flips the state so the user gets immediate feedback even
 * on slow networks.
 */
export function TmWatchButton({ taskId }: { taskId: string }) {
  const { canWrite } = useSectionAccess("tm_tasks");
  const toast = useToast();
  const { t } = useTranslation();
  const { state } = useAuth();
  const [watchers, setWatchers] = useState<TmTaskWatcherView[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const list = await tmApi.listWatchers(taskId);
      setWatchers(list);
    } catch {
      setWatchers([]);
    }
  }, [taskId]);

  useEffect(() => { void refresh(); }, [refresh]);

  const myId = state.status === "authenticated" ? state.user.id : null;
  const watching = !!myId && watchers.some((w) => w.userId === myId);

  async function toggle() {
    if (!canWrite) return;
    if (!myId) return;
    setBusy(true);
    // Optimistic flip.
    setWatchers((prev) => watching
      ? prev.filter((w) => w.userId !== myId)
      : [...prev, { userId: myId, name: t("tm.watch.you", "You"), email: "" }]);
    try {
      if (watching) await tmApi.unwatch(taskId);
      else          await tmApi.watch(taskId);
      await refresh();
    } catch (err) {
      toast.error((err as ApiError).message);
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button
          type="button"
          onClick={() => void toggle()}
          disabled={!canWrite || busy}
          style={{
            ...btnBase,
            background: watching ? "var(--brand-primary)" : "var(--surface)",
            color: watching ? "var(--on-primary)" : "var(--ink-2)",
            borderColor: watching ? "var(--brand-primary)" : "var(--border)",
          }}
        >
          {watching ? `★ ${t("tm.watch.watching", "Watching")}` : `☆ ${t("tm.watch.watch", "Watch")}`}
        </button>
        {watchers.length > 0 && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            style={countBtn}
            title={open ? t("tm.watch.hideList", "Hide watchers") : t("tm.watch.showList", "Show watchers")}
          >
            {watchers.length} {t("tm.tasks.detail.watchers", "Watchers")} {open ? "▴" : "▾"}
          </button>
        )}
      </div>
      {open && watchers.length > 0 && (
        <ul style={listStyle}>
          {watchers.map((w) => (
            <li key={w.userId} style={{ fontSize: 12, color: "var(--ink-2)", padding: "3px 0" }}>
              {w.name}<span style={{ color: "var(--ink-4)" }}> · {w.email}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const btnBase: React.CSSProperties = {
  padding: "5px 12px", borderRadius: 6, border: "1px solid var(--border)",
  fontSize: 12.5, fontWeight: 600, cursor: "pointer", transition: "all .12s ease",
};
const countBtn: React.CSSProperties = {
  background: "transparent", border: "none", color: "var(--ink-3)",
  fontSize: 12, cursor: "pointer", padding: "0 4px",
};
const listStyle: React.CSSProperties = {
  listStyle: "none", padding: "6px 10px", margin: 0,
  background: "var(--surface-2)", borderRadius: 6, border: "1px solid var(--border)",
};
